/**
 * Step 5: exact model covariate bindings.
 * R variable names come from the actual script; model bindings are the only
 * persisted runtime mapping. Patient-only inputs may intentionally emit no R
 * variable.
 */
import React, { useEffect, useRef } from 'react';
import { Alert, Empty, Space, Tag, Typography } from 'antd';
import ModelCovariateBindingEditor, {
  type ModelCovariateBindingEditorHandle,
} from '../ModelCovariateBindingEditor';
import { useWizard } from './WizardContext';

const { Text } = Typography;

interface Step5SaveHandle {
  validateFields: () => Promise<void>;
  getFieldsValue: () => Record<string, unknown>;
  saveBindings: () => Promise<void>;
}

interface Step5Props {
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  formRef?: React.MutableRefObject<Step5SaveHandle | null>;
}

const Step5Covariates: React.FC<Step5Props> = ({ data, formRef }) => {
  const { modelId } = useWizard();
  const bindingEditorRef = useRef<ModelCovariateBindingEditorHandle | null>(null);
  const rawCovariates = Array.isArray(data.r_covariates_raw)
    ? data.r_covariates_raw.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
    : [];

  useEffect(() => {
    if (!formRef) return undefined;
    const handle: Step5SaveHandle = {
      validateFields: async () => undefined,
      getFieldsValue: () => data,
      saveBindings: async () => {
        if (!bindingEditorRef.current) {
          throw new Error('协变量绑定编辑器尚未就绪');
        }
        await bindingEditorRef.current.save({ silent: true });
      },
    };
    formRef.current = handle;
    return () => {
      if (formRef.current === handle) formRef.current = null;
    };
  }, [data, formRef]);

  if (!modelId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="请先保存模型基本信息"
        description="获取模型 ID 后，才能保存模型级精确 R 变量绑定。"
      />
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Text strong>R 脚本协变量（只读）</Text>
        <Alert
          type="info"
          showIcon
          style={{ marginBlock: 8 }}
          message="此清单直接来自 R 脚本 covariates = c(...)。下方绑定必须使用完全相同的变量名和大小写。"
        />
        {rawCovariates.length ? (
          <Space wrap>
            {rawCovariates.map((covariate) => (
              <Tag key={covariate} color="blue">{covariate}</Tag>
            ))}
          </Space>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未从 R 脚本解析到协变量" />
        )}
      </div>

      <ModelCovariateBindingEditor ref={bindingEditorRef} modelId={modelId} />
    </Space>
  );
};

export default Step5Covariates;
